import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { CollectionsProvider } from './context/CollectionsContext';
import { LocationProvider } from './context/LocationContext';
import { ThemeProvider } from './context/ThemeContext';
import Home from './pages/Home';
import CategoriesPage from './pages/CategoriesPage';
import CategoryPage from './pages/CategoryPage';
import PlacePage from './pages/PlacePage';
import GiveawayPage from './pages/GiveawayPage';
import ProfilePage from './pages/ProfilePage';
import FavoritesPage from './pages/FavoritesPage';
import NearbyPage from './pages/NearbyPage';
import SearchPage from './pages/SearchPage';
import CollectionPage from './pages/CollectionPage';
import RoutesPage from './pages/RoutesPage';
import RouteDetailPage from './pages/RouteDetailPage';
import RouteWalkingPage from './pages/RouteWalkingPage';
import UserRouteWalkingPage from './pages/UserRouteWalkingPage';
import ProtectedRoute from './components/ProtectedRoute';
import DevModeIndicator from './components/DevModeIndicator';
import Toast from './components/Toast';
import Layout from './components/Layout';
import telegramService from './services/telegram';
import useTelegramNavigation from './hooks/useTelegramNavigation';

function AppContent() {
  useTelegramNavigation();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  useEffect(() => {
    // Обработка deep linking для шеринга мест, подборок и маршрутов
    const startParam = telegramService.getStartParam();

    if (startParam && startParam.startsWith('place-')) {
      const placeSlug = startParam.substring(6); // Убираем префикс "place-"
      navigate(`/place/${placeSlug}`, { replace: true });
    } else if (startParam && startParam.startsWith('collection-')) {
      const collectionSlug = startParam.substring(11); // Убираем префикс "collection-"
      navigate(`/collection/${collectionSlug}`, { replace: true });
    } else if (startParam && startParam.startsWith('route-')) {
      const routeSlug = startParam.substring(6); // Убираем префикс "route-"
      navigate(`/routes/${routeSlug}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Выполняется только один раз при монтировании

  // Показываем пустой экран пока идёт авторизация
  if (authLoading) {
    return null;
  }

  return (
    <div className="App max-w-md mx-auto" style={{
      paddingTop: 'calc(var(--safe-area-top) + var(--content-safe-area-top))',
      paddingBottom: 'calc(var(--safe-area-bottom) + 85px)'
    }}>
      <DevModeIndicator />
      <Toast />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/category" element={<CategoriesPage />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/place/:slug" element={<PlacePage />} />
          <Route path="/giveaway/:slug" element={<GiveawayPage />} />
          <Route path="/collection/:slug" element={<CollectionPage />} />
          <Route path="/routes" element={<RoutesPage />} />
          <Route path="/routes/:slug" element={<RouteDetailPage />} />
          <Route path="/routes/:slug/walk" element={<RouteWalkingPage />} />
          <Route
            path="/my-route/:id/walk"
            element={
              <ProtectedRoute>
                <UserRouteWalkingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/nearby" element={<NearbyPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </div>
  );
}

function App() {
  const [isTelegramReady, setIsTelegramReady] = React.useState(false);

  useEffect(() => {
    // Отключаем браузерное восстановление скролла
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Initialize Telegram Web App and wait for ready
    const initTelegram = async () => {
      await telegramService.waitForReady();
      setIsTelegramReady(true);
    };

    initTelegram();
  }, []);

  // Ждем готовности Telegram SDK перед рендером
  if (!isTelegramReady) {
    return null; // Или можно показать минимальный loader
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <FavoritesProvider>
          <CollectionsProvider>
            <LocationProvider>
              <Router>
                <AppContent />
              </Router>
            </LocationProvider>
          </CollectionsProvider>
        </FavoritesProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
